import { FC, useEffect, useState } from 'react';
import { Form, Input, Modal, Select, Switch } from 'antd';
import {
  createBillingAddress,
  updateBillingAddress
} from 'services/api/invoicingBillingAddresses';
import { BillingAddress, BillingAddressInput } from 'types/api/invoicing';

import { useCountryOptions } from '../../../useCountryOptions';
import { showErrorMessage } from 'utils/showErrorMessage';

interface IProps {
  open: boolean;
  patientId: number;
  address: BillingAddress | null; // null → create
  onClose: () => void;
  onSuccess: () => void;
}

type FormValues = {
  fullName: string;
  company?: string;
  vatId?: string;
  line1: string;
  line2?: string;
  city: string;
  state?: string;
  postalCode?: string;
  country: string;
  isDefault: boolean;
};

const AddressModal: FC<IProps> = ({
  open,
  patientId,
  address,
  onClose,
  onSuccess
}) => {
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const countryOptions = useCountryOptions();
  const isEdit = !!address;

  useEffect(() => {
    if (!open) {
      return;
    }

    if (address) {
      form.setFieldsValue({
        fullName: address.fullName,
        company: address.company ?? undefined,
        vatId: address.vatId ?? undefined,
        line1: address.line1,
        line2: address.line2 ?? undefined,
        city: address.city,
        state: address.state ?? undefined,
        postalCode: address.postalCode ?? undefined,
        country: address.country,
        isDefault: address.isDefault
      });
    } else {
      form.resetFields();
    }
  }, [open, address, form]);

  const onFinish = async (values: FormValues) => {
    const payload = {
      fullName: values.fullName.trim(),
      company: values.company?.trim() || null,
      vatId: values.vatId?.trim() || null,
      line1: values.line1.trim(),
      line2: values.line2?.trim() || null,
      city: values.city.trim(),
      state: values.state?.trim() || null,
      postalCode: values.postalCode?.trim() || null,
      country: values.country.trim().toUpperCase(),
      isDefault: values.isDefault
    };

    try {
      setLoading(true);

      if (address) {
        await updateBillingAddress(address.publicId, payload);
      } else {
        await createBillingAddress({
          patientId,
          ...payload
        } as BillingAddressInput);
      }

      onSuccess();
      onClose();
    } catch (err) {
      showErrorMessage(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={isEdit ? 'Save' : 'Create'}
      title={isEdit ? 'Edit billing address' : 'New billing address'}
      confirmLoading={loading}
      closable={!loading}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{ isDefault: false }}
      >
        <Form.Item
          label="Full name"
          name="fullName"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Company" name="company">
          <Input />
        </Form.Item>
        <Form.Item label="VAT ID" name="vatId">
          <Input />
        </Form.Item>
        <Form.Item
          label="Address line 1"
          name="line1"
          rules={[{ required: true }]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Address line 2" name="line2">
          <Input />
        </Form.Item>
        <Form.Item label="City" name="city" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label="State / region" name="state">
          <Input />
        </Form.Item>
        <Form.Item label="Postal code" name="postalCode">
          <Input />
        </Form.Item>
        <Form.Item label="Country" name="country" rules={[{ required: true }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select country"
            options={countryOptions}
          />
        </Form.Item>
        <Form.Item
          label="Default address"
          name="isDefault"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddressModal;
